import { useMemo } from "react";
import { useQuery, type ApolloError } from "@apollo/client";
import type { DatasourceInstanceEdge } from "./types";
import { GET_DATASOURCES_INSTANCES } from "./APIs/DataSourcesApi";
import { isDatasourceDefinitionHidden } from "./DataSourcesUtils";

export type DatasourcesInstancesFetchPolicy =
  | "cache-first"
  | "no-cache"
  | "cache-and-network"
  | "network-only";

export interface UseDatasourcesInstancesOptions {
  queryOptions?: {
    fetchPolicy?: DatasourcesInstancesFetchPolicy;
  };
  onlyVisible?: boolean;
}

export type { DatasourceInstanceEdge, DatasourceInstanceNode } from "./types";

export interface UseDatasourcesInstancesResult {
  datasourcesInstancesEdges: DatasourceInstanceEdge[];
  datasourcesInstancesError: ApolloError | undefined;
  datasourcesInstancesLoading: boolean;
  refetchDatasourcesInstances: () => void;
}

/**
 * Fetches the list of datasource instances (no threat intelligence).
 * Static data - safe to cache and provide from top-level provider.
 */
const useDatasourcesInstances = ({
  queryOptions,
  onlyVisible,
}: UseDatasourcesInstancesOptions = {}): UseDatasourcesInstancesResult => {
  const { data, error, loading, refetch } = useQuery(
    GET_DATASOURCES_INSTANCES,
    {
      fetchPolicy: queryOptions?.fetchPolicy ?? "cache-first",
      notifyOnNetworkStatusChange: true,
      ...queryOptions,
    }
  );

  const edges = useMemo((): DatasourceInstanceEdge[] => {
    const all: DatasourceInstanceEdge[] = data?.datasources?.edges ?? [];
    return onlyVisible
      ? all.filter(
          ({ node }) =>
            !isDatasourceDefinitionHidden(node.datasource_definition)
        )
      : all;
  }, [data?.datasources?.edges, onlyVisible]);

  return {
    datasourcesInstancesEdges: edges,
    datasourcesInstancesError: error ?? undefined,
    datasourcesInstancesLoading: loading,
    refetchDatasourcesInstances: refetch ?? (() => {}),
  };
};

export default useDatasourcesInstances;
